import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Trash2, Plus, Printer, ShieldCheck, FileBadge2, X, Save, ArrowLeft, AlertTriangle, Banknote, Eye,
} from "lucide-react";
import { toast } from "sonner";
import POSReceiptPreview from "@/components/POSReceiptPreview";
import { printReceiptPdf, buildReceiptFilename } from "@/lib/receiptPdf";
import MedicalCertificatePreview, { DEFAULT_TEMPLATE } from "@/components/MedicalCertificatePreview";

const PESO = "\u20B1";
const PAYMENT_METHODS = ["Cash", "GCash", "Card", "Bank Transfer"];

function fmtMoney(n) {
  const v = Number(n || 0);
  return `${PESO}${v.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function computeLineTotal(li) {
  const qty = Number(li.quantity) || 0;
  const price = Number(li.unit_price) || 0;
  const pct = Math.max(0, Math.min(Number(li.discount_percent) || 0, 100));
  let g = qty * price * (1 - pct / 100);
  g = Math.max(0, g - (Number(li.discount_amount) || 0));
  return Math.round(g * 100) / 100;
}

function computeTotals(line_items, txDiscAmt, txDiscPct) {
  let gross = 0;
  let lineDisc = 0;
  for (const li of line_items) {
    const qty = Number(li.quantity) || 0;
    const price = Number(li.unit_price) || 0;
    const pct = Math.max(0, Math.min(Number(li.discount_percent) || 0, 100));
    const lineGross = qty * price;
    gross += lineGross;
    const afterPct = lineGross * (1 - pct / 100);
    lineDisc += (lineGross - afterPct) + Math.max(0, Number(li.discount_amount) || 0);
  }
  lineDisc = Math.min(lineDisc, gross);
  const subAfterLine = gross - lineDisc;
  const txPct = Math.max(0, Math.min(Number(txDiscPct) || 0, 100));
  const txAfterPct = subAfterLine * (1 - txPct / 100);
  let txDisc = (subAfterLine - txAfterPct) + Math.max(0, Number(txDiscAmt) || 0);
  txDisc = Math.min(subAfterLine, txDisc);
  const subtotal = subAfterLine - txDisc;
  const grand = subtotal; // 0 tax
  const effPct = subAfterLine > 0 ? Math.round((txDisc / subAfterLine) * 10000) / 100 : 0;
  return {
    gross_total: Math.round(gross * 100) / 100,
    line_discount_total: Math.round(lineDisc * 100) / 100,
    transaction_discount_total: Math.round(txDisc * 100) / 100,
    subtotal: Math.round(subtotal * 100) / 100,
    tax_amount: 0,
    grand_total: Math.round(grand * 100) / 100,
    effective_transaction_discount_percent: effPct,
  };
}

export default function POSTransaction() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A user can hold several roles (a doctor who also runs the cashier). The
  // single `role` is only the highest-priority one, so authorize POS actions
  // against the full set — matching the backend, which checks the roles array.
  const roles = user?.active_roles || user?.roles || (user?.role ? [user.role] : []);
  const isAdminLike = roles.includes("admin") || roles.includes("hospital_admin");
  const isReception = roles.includes("reception");
  const isDoctor = roles.includes("doctor");
  const isCashier = roles.includes("cashier");
  const canCollectPayment = isAdminLike || isReception || isCashier;

  const [tx, setTx] = useState(null);
  const [services, setServices] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const [printSettings, setPrintSettings] = useState({});
  const [receiptHeader, setReceiptHeader] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Editable state
  const [lineItems, setLineItems] = useState([]);
  const [txDiscAmt, setTxDiscAmt] = useState(0);
  const [txDiscPct, setTxDiscPct] = useState(0);
  const [discountReason, setDiscountReason] = useState("");
  const [notes, setNotes] = useState("");

  // Payment dialog
  const [payOpen, setPayOpen] = useState(false);
  const [payments, setPayments] = useState([{ method: "Cash", amount: 0, reference: "" }]);
  // Charity case: close the bill without collecting anything.
  const [proBono, setProBono] = useState(false);
  const [proBonoReason, setProBonoReason] = useState("");

  // Void dialog
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [restoreInventory, setRestoreInventory] = useState(true);

  // Approval dialog (admin)
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveNote, setApproveNote] = useState("");

  // Receipt dialog
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [printingReceipt, setPrintingReceipt] = useState(false);
  const receiptRef = useRef(null);

  // Print the receipt as a roll-sized PDF so Chrome can't add its URL/date/page
  // headers or the user's margin setting — no per-station printer setup needed.
  const printReceipt = async () => {
    const el = receiptRef.current?.querySelector("[data-testid='pos-receipt-page']") || receiptRef.current;
    if (!el) { window.print(); return; }
    setPrintingReceipt(true);
    try {
      await printReceiptPdf({
        element: el,
        paperWidth: receiptHeader?.paper_width || tx?.receipt_header_snapshot?.paper_width || "80mm",
        filename: buildReceiptFilename(tx),
      });
    } catch (e) {
      toast.error("Could not build the receipt PDF — using the browser print instead");
      window.print();
    } finally {
      setPrintingReceipt(false);
    }
  };

  // Add custom item dialog
  const [customOpen, setCustomOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ name: "", unit_price: 0, quantity: 1, description: "" });

  // Medical Certificate dialog
  const [mcOpen, setMcOpen] = useState(false);
  const [mcForm, setMcForm] = useState({ diagnosis: "", remarks: "", purpose: "", rest_days: 0, visit_date: "", issue_date: "", date_text: "", body_template: DEFAULT_TEMPLATE });
  const [mcPreviewOpen, setMcPreviewOpen] = useState(false);
  const [activeCert, setActiveCert] = useState(null);
  const [certs, setCerts] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, svc, meds, ps, cs, rh] = await Promise.all([
        api.get(`/pos/transactions/${id}`),
        api.get("/pos/services"),
        api.get("/medicines"),
        api.get("/print-settings"),
        api.get(`/pos/transactions/${id}/medical-certificates`).catch(() => ({ data: [] })),
        api.get("/pos-receipt-header").catch(() => ({ data: {} })),
      ]);
      setTx(t.data);
      setServices(svc.data || []);
      setMedicines(meds.data || []);
      setPrintSettings(ps.data || {});
      setReceiptHeader(rh.data || {});
      setLineItems(t.data.line_items || []);
      setTxDiscAmt(Number(t.data.transaction_discount_amount) || 0);
      setTxDiscPct(Number(t.data.transaction_discount_percent) || 0);
      setDiscountReason(t.data.discount_reason || "");
      setNotes(t.data.notes || "");
      setCerts(cs.data || []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Auto-open print if ?print=1
  useEffect(() => {
    if (searchParams.get("print") === "1" && tx && tx.status === "paid") {
      setReceiptOpen(true);
    }
  }, [searchParams, tx]);

  const totals = useMemo(() => computeTotals(lineItems, txDiscAmt, txDiscPct), [lineItems, txDiscAmt, txDiscPct]);
  // Discount approval disabled for now — payment proceeds regardless of discount amount.
  const needsApproval = false;
  const editable = tx && (tx.status === "draft" || tx.status === "waiting_payment");

  // ---- Line item helpers ----
  const addLineItem = (item) => setLineItems((prev) => [...prev, { ...item, _newId: crypto.randomUUID() }]);
  const removeLineItem = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));
  const updateLineItem = (idx, patch) => setLineItems((prev) => prev.map((li, i) => i === idx ? { ...li, ...patch } : li));

  const addServiceShortcut = (svc) => {
    addLineItem({
      item_type: "service",
      service_id: svc.id,
      name: svc.name,
      category: svc.category,
      quantity: 1,
      unit_price: svc.default_price,
      discount_amount: 0,
      discount_percent: 0,
    });
  };

  const addMedicine = (med) => {
    addLineItem({
      item_type: "medicine",
      medicine_id: med.id,
      name: med.medicine_name,
      description: [med.strength, med.dosage_form].filter(Boolean).join(" "),
      category: "Medicine",
      quantity: 1,
      unit: med.dosage_form || "unit",
      unit_price: med.unit_price || 0,
      discount_amount: 0,
      discount_percent: 0,
    });
  };

  const addCustomItem = () => {
    if (!customForm.name) { toast.error("Item name required"); return; }
    addLineItem({
      item_type: "custom",
      name: customForm.name,
      description: customForm.description,
      category: "Other",
      quantity: Number(customForm.quantity) || 1,
      unit_price: Number(customForm.unit_price) || 0,
      discount_amount: 0,
      discount_percent: 0,
    });
    setCustomOpen(false);
    setCustomForm({ name: "", unit_price: 0, quantity: 1, description: "" });
  };

  // ---- Save / Submit ----
  const saveDraft = async (status) => {
    if (!tx) return;
    setBusy(true);
    try {
      const payload = {
        patient_id: tx.patient_id,
        doctor_id: tx.doctor_id || "",
        queue_id: tx.queue_id || "",
        prescription_id: tx.prescription_id || "",
        line_items: lineItems.map((li) => ({
          item_type: li.item_type,
          medicine_id: li.medicine_id || "",
          service_id: li.service_id || "",
          name: li.name,
          description: li.description || "",
          quantity: Number(li.quantity) || 0,
          unit: li.unit || "",
          unit_price: Number(li.unit_price) || 0,
          discount_amount: Number(li.discount_amount) || 0,
          discount_percent: Number(li.discount_percent) || 0,
          category: li.category || "",
        })),
        transaction_discount_amount: Number(txDiscAmt) || 0,
        transaction_discount_percent: Number(txDiscPct) || 0,
        discount_reason: discountReason,
        tax_rate: 0,
        notes,
        status,
      };
      const res = await api.put(`/pos/transactions/${id}`, payload);
      setTx(res.data);
      setLineItems(res.data.line_items || []);
      toast.success(status === "waiting_payment" ? "Sent to reception" : "Draft saved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Pay ----
  const openPayDialog = () => {
    if (!editable) return;
    // Pre-fill first payment with full grand total in Cash
    setPayments([{ method: "Cash", amount: totals.grand_total, reference: "" }]);
    setPayOpen(true);
  };
  const addPayment = () => setPayments((p) => [...p, { method: "Cash", amount: 0, reference: "" }]);
  const removePayment = (idx) => setPayments((p) => p.filter((_, i) => i !== idx));
  const updatePayment = (idx, patch) => setPayments((p) => p.map((x, i) => i === idx ? { ...x, ...patch } : x));
  const paymentSum = useMemo(() => payments.reduce((s, p) => s + (Number(p.amount) || 0), 0), [payments]);

  const submitPay = async () => {
    if (!proBono && paymentSum + 1e-2 < totals.grand_total) {
      toast.error(`Insufficient payment. Required ${fmtMoney(totals.grand_total)}`);
      return;
    }
    if (proBono && !window.confirm(
      `Close this as PRO BONO?\n\nNothing will be collected. ${fmtMoney(totals.grand_total)} will be recorded as waived, not as revenue.`
    )) return;
    setBusy(true);
    try {
      const res = await api.post(`/pos/transactions/${id}/pay`, {
        pro_bono: proBono,
        pro_bono_reason: proBono ? proBonoReason : "",
        // The backend rejects payments alongside pro bono — send none.
        payments: proBono ? [] : payments.map((p) => ({
          method: p.method,
          amount: Number(p.amount) || 0,
          reference: p.reference || "",
        })),
        line_items: lineItems.map((li) => ({
          item_type: li.item_type,
          medicine_id: li.medicine_id || "",
          service_id: li.service_id || "",
          name: li.name,
          description: li.description || "",
          quantity: Number(li.quantity) || 0,
          unit: li.unit || "",
          unit_price: Number(li.unit_price) || 0,
          discount_amount: Number(li.discount_amount) || 0,
          discount_percent: Number(li.discount_percent) || 0,
          category: li.category || "",
        })),
        transaction_discount_amount: Number(txDiscAmt) || 0,
        transaction_discount_percent: Number(txDiscPct) || 0,
        discount_reason: discountReason,
      });
      setTx(res.data);
      setLineItems(res.data.line_items || []);
      setPayOpen(false);
      toast.success("Payment recorded. Inventory updated.");
      setReceiptOpen(true);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Void ----
  const submitVoid = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/pos/transactions/${id}/void`, {
        reason: voidReason,
        restore_inventory: restoreInventory,
      });
      setTx(res.data);
      setVoidOpen(false);
      toast.success(`Transaction voided${restoreInventory && res.data.void_restored_inventory ? " · Inventory restored" : ""}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Approve discount ----
  const approveDiscount = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/pos/transactions/${id}/approve-discount`, {
        approved: true,
        note: approveNote,
      });
      setTx(res.data);
      setApproveOpen(false);
      toast.success("Discount approved");
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  // ---- Medical Certificate ----
  const generateCertificate = async (alsoSave = true) => {
    setBusy(true);
    try {
      const res = await api.post(`/pos/transactions/${id}/medical-certificate`, mcForm);
      if (alsoSave) {
        toast.success("Medical certificate saved");
        const lst = await api.get(`/pos/transactions/${id}/medical-certificates`);
        setCerts(lst.data || []);
        setActiveCert(res.data);
        setMcOpen(false);
        setMcPreviewOpen(true);
      }
      return res.data;
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const openCertificate = (c) => {
    setActiveCert(c);
    setMcPreviewOpen(true);
  };

  if (loading || !tx) return <div className="p-6 text-slate-500" data-testid="pos-tx-loading">Loading…</div>;

  return (
    <div className="p-5 lg:p-7 space-y-5" data-testid="pos-transaction-page">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/pos")} data-testid="btn-back">
            <ArrowLeft size={14} className="mr-1" /> POS
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-slate-900" data-testid="tx-number">
              {tx.transaction_number}
              <Badge variant="outline" className="ml-2">{tx.status}</Badge>
              {tx.discount_approval_required ? (
                <span className="ml-2 text-xs text-rose-700 font-semibold uppercase tracking-wider">Approval needed</span>
              ) : null}
            </h1>
            <div className="text-xs text-slate-500 mt-0.5">
              Patient: <span className="font-medium text-slate-700">{tx.patient_snapshot?.full_name || "—"}</span>
              {" · "}Doctor: <span className="font-medium text-slate-700">{tx.doctor_snapshot?.name || "—"}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {editable ? (
            <Button variant="outline" onClick={() => saveDraft("draft")} disabled={busy} data-testid="btn-save-draft">
              <Save size={14} className="mr-1" /> Save Draft
            </Button>
          ) : null}
          {editable && isDoctor && !canCollectPayment ? (
            <Button onClick={() => saveDraft("waiting_payment")} disabled={busy} data-testid="btn-send-reception">
              Send to Reception
            </Button>
          ) : null}
          {editable && canCollectPayment ? (
            <Button onClick={openPayDialog} disabled={busy || lineItems.length === 0} data-testid="btn-open-pay">
              <Banknote size={14} className="mr-1" /> Pay {fmtMoney(totals.grand_total)}
            </Button>
          ) : null}
          {(tx.status === "paid") ? (
            <Button variant="outline" onClick={() => setReceiptOpen(true)} data-testid="btn-print-receipt">
              <Printer size={14} className="mr-1" /> Print Receipt
            </Button>
          ) : null}
          {(tx.status === "paid" || editable) && (isAdminLike || isReception) ? (
            <Button variant="outline" onClick={() => setVoidOpen(true)} disabled={busy} data-testid="btn-void">
              <X size={14} className="mr-1" /> Void
            </Button>
          ) : null}
          {tx.discount_approval_required && isAdminLike ? (
            <Button variant="outline" onClick={() => setApproveOpen(true)} data-testid="btn-approve-discount">
              <ShieldCheck size={14} className="mr-1 text-emerald-700" /> Approve Discount
            </Button>
          ) : null}
        </div>
      </div>

      {/* Service shortcuts */}
      {editable ? (
        <Card className="p-4" data-testid="service-shortcuts">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-semibold">Quick Add</div>
          <div className="flex gap-2 flex-wrap">
            {services.filter((s) => s.shortcut_enabled).map((s) => (
              <Button
                key={s.id}
                size="sm"
                variant="outline"
                onClick={() => addServiceShortcut(s)}
                data-testid={`shortcut-${s.id}`}
              >
                <Plus size={12} className="mr-1" /> {s.name}
                <span className="ml-1 text-slate-500 text-xs">{fmtMoney(s.default_price)}</span>
              </Button>
            ))}
            <Button size="sm" variant="outline" onClick={() => setCustomOpen(true)} data-testid="btn-add-custom">
              <Plus size={12} className="mr-1" /> Custom Item
            </Button>
            <MedicineAdder medicines={medicines} onAdd={addMedicine} />
          </div>
        </Card>
      ) : null}

      {/* Line items table */}
      <Card className="p-0 overflow-hidden" data-testid="line-items-card">
        <div className="px-5 py-3 border-b flex items-center justify-between">
          <div className="font-semibold text-slate-900">Line Items</div>
          <div className="text-xs text-slate-500">{lineItems.length} items</div>
        </div>
        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-3 py-2 font-medium">#</th>
                <th className="text-left px-3 py-2 font-medium">Item</th>
                <th className="text-right px-3 py-2 font-medium w-24">Qty</th>
                <th className="text-right px-3 py-2 font-medium w-32">Unit Price</th>
                <th className="text-right px-3 py-2 font-medium w-24">Disc %</th>
                <th className="text-right px-3 py-2 font-medium w-32">Line Total</th>
                {editable ? <th className="w-12"></th> : null}
              </tr>
            </thead>
            <tbody className="divide-y">
              {lineItems.length === 0 ? (
                <tr><td colSpan={editable ? 7 : 6} className="text-center text-slate-400 italic py-8">No items yet. Add from shortcuts above.</td></tr>
              ) : (
                lineItems.map((li, i) => (
                  <tr key={li.id || li._newId || i} data-testid={`line-row-${i}`}>
                    <td className="px-3 py-2 text-slate-500">{i + 1}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-slate-900">{li.name}</div>
                      {li.description ? <div className="text-xs text-slate-500">{li.description}</div> : null}
                      <div className="text-[10px] uppercase tracking-wider text-slate-400">{li.item_type}{li.category ? ` · ${li.category}` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editable ? (
                        <Input
                          type="number" min="0" step="1"
                          className="h-8 text-right"
                          value={li.quantity}
                          onChange={(e) => updateLineItem(i, { quantity: e.target.value })}
                          data-testid={`qty-${i}`}
                        />
                      ) : li.quantity}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editable ? (
                        <Input
                          type="number" min="0" step="0.01"
                          className="h-8 text-right"
                          value={li.unit_price}
                          onChange={(e) => updateLineItem(i, { unit_price: e.target.value })}
                          data-testid={`price-${i}`}
                        />
                      ) : fmtMoney(li.unit_price)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {editable ? (
                        <Input
                          type="number" min="0" max="100" step="1"
                          className="h-8 text-right"
                          value={li.discount_percent || 0}
                          onChange={(e) => updateLineItem(i, { discount_percent: e.target.value })}
                          data-testid={`disc-${i}`}
                        />
                      ) : `${li.discount_percent || 0}%`}
                    </td>
                    <td className="px-3 py-2 text-right font-semibold" data-testid={`linetotal-${i}`}>
                      {fmtMoney(computeLineTotal(li))}
                    </td>
                    {editable ? (
                      <td className="px-2 py-2 text-right">
                        <Button size="sm" variant="ghost" onClick={() => removeLineItem(i)} data-testid={`remove-${i}`}>
                          <Trash2 size={14} className="text-rose-600" />
                        </Button>
                      </td>
                    ) : null}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Discount / Notes / Totals */}
      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-4 lg:col-span-2 space-y-3" data-testid="discount-notes-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Transaction Discount & Notes</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Discount Amount (₱)</Label>
              <Input
                type="number" min="0" step="0.01"
                value={txDiscAmt}
                onChange={(e) => setTxDiscAmt(e.target.value)}
                disabled={!editable}
                data-testid="tx-disc-amt"
              />
            </div>
            <div>
              <Label className="text-xs">Discount Percent</Label>
              <Input
                type="number" min="0" max="100" step="1"
                value={txDiscPct}
                onChange={(e) => setTxDiscPct(e.target.value)}
                disabled={!editable}
                data-testid="tx-disc-pct"
              />
            </div>
          </div>
          {needsApproval ? (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2 flex items-start gap-2" data-testid="discount-warning">
              <AlertTriangle size={14} className="mt-0.5" />
              <div>
                Discount exceeds 20% (effective: {totals.effective_transaction_discount_percent}%). Hospital Admin approval is required before payment.
              </div>
            </div>
          ) : null}
          <div>
            <Label className="text-xs">Discount Reason</Label>
            <Input
              value={discountReason}
              onChange={(e) => setDiscountReason(e.target.value)}
              disabled={!editable}
              placeholder="e.g. Senior citizen, PWD, employee"
              data-testid="disc-reason"
            />
          </div>
          <div>
            <Label className="text-xs">Internal Notes</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={!editable}
              data-testid="tx-notes"
            />
          </div>
        </Card>

        <Card className="p-4 space-y-2" data-testid="totals-card">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Totals</div>
          <Row label="Gross" value={fmtMoney(totals.gross_total)} />
          {totals.line_discount_total > 0 ? <Row label="Line Discounts" value={`-${fmtMoney(totals.line_discount_total)}`} red /> : null}
          {totals.transaction_discount_total > 0 ? <Row label="Transaction Discount" value={`-${fmtMoney(totals.transaction_discount_total)}`} red /> : null}
          <Row label="Subtotal" value={fmtMoney(totals.subtotal)} />
          <Row label="Tax (0%)" value={fmtMoney(0)} muted />
          <div className="border-t pt-2 mt-2">
            <Row label="Grand Total" value={fmtMoney(totals.grand_total)} big testid="totals-grand" />
          </div>
        </Card>
      </div>

      {/* Medical Certificates section */}
      {(certs.length > 0 || lineItems.some((li) => li.name && li.name.toLowerCase().includes("certificate"))) ? (
        <Card className="p-4" data-testid="medcert-section">
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold text-slate-800 flex items-center gap-2">
              <FileBadge2 size={14} /> Medical Certificate
            </div>
            <Button size="sm" variant="outline" onClick={() => setMcOpen(true)} data-testid="btn-generate-mc">
              <Plus size={12} className="mr-1" /> Generate Medical Certificate
            </Button>
          </div>
          {certs.length === 0 ? (
            <div className="text-xs text-slate-500 italic">No certificate generated yet. Click "Generate Medical Certificate" to create one.</div>
          ) : (
            <div className="space-y-1">
              {certs.map((c) => (
                <div key={c.id} className="flex items-center justify-between border rounded px-3 py-2 text-sm" data-testid={`cert-row-${c.id}`}>
                  <div>
                    <div className="font-medium">MC# {c.id?.slice(0, 8)}</div>
                    <div className="text-xs text-slate-500">{c.purpose || c.diagnosis || "—"} · Issued {c.issue_date || c.created_at?.slice(0,10)}</div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => openCertificate(c)} data-testid={`cert-view-${c.id}`}>
                      <Eye size={12} className="mr-1" /> View / Print
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}

      {/* ==== Dialogs ==== */}

      {/* Custom item */}
      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent data-testid="custom-item-dialog">
          <DialogHeader>
            <DialogTitle>Add Custom Item</DialogTitle>
            <DialogDescription>Add a free-form line item (e.g. lab fee, procedure).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={customForm.name} onChange={(e) => setCustomForm({...customForm, name: e.target.value})} data-testid="custom-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Qty</Label><Input type="number" min="1" value={customForm.quantity} onChange={(e) => setCustomForm({...customForm, quantity: e.target.value})} data-testid="custom-qty" /></div>
              <div><Label>Unit Price (₱)</Label><Input type="number" min="0" step="0.01" value={customForm.unit_price} onChange={(e) => setCustomForm({...customForm, unit_price: e.target.value})} data-testid="custom-price" /></div>
            </div>
            <div><Label>Description (optional)</Label><Input value={customForm.description} onChange={(e) => setCustomForm({...customForm, description: e.target.value})} data-testid="custom-desc" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCustomOpen(false)}>Cancel</Button>
            <Button onClick={addCustomItem} data-testid="btn-add-custom-submit">Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="max-w-lg" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle>Collect Payment · {fmtMoney(totals.grand_total)}</DialogTitle>
            <DialogDescription>Split payment is supported. Sum of methods must be ≥ Grand Total.</DialogDescription>
          </DialogHeader>
          {/* Pro bono short-circuits the whole payment section. */}
          <div
            className={`flex items-start gap-2 border rounded p-3 ${proBono ? "bg-amber-50 border-amber-300" : "bg-slate-50 border-slate-200"}`}
            data-testid="pro-bono-row"
          >
            <Checkbox
              id="pro-bono"
              checked={proBono}
              onCheckedChange={(v) => setProBono(!!v)}
              data-testid="pro-bono-check"
            />
            <div className="flex-1 min-w-0">
              <label htmlFor="pro-bono" className="text-sm font-medium text-slate-900 cursor-pointer">
                Pro bono — collect nothing
              </label>
              <div className="text-xs text-slate-500">
                Closes the bill with ₱0 collected. {fmtMoney(totals.grand_total)} is recorded as waived,
                and is kept out of sales and the day's collection.
              </div>
              {proBono ? (
                <Input
                  className="mt-2"
                  placeholder="Reason (optional) — e.g. indigent patient, charity mission"
                  value={proBonoReason}
                  onChange={(e) => setProBonoReason(e.target.value)}
                  data-testid="pro-bono-reason"
                />
              ) : null}
            </div>
          </div>

          <div className={`space-y-2 max-h-[50vh] overflow-y-auto ${proBono ? "opacity-40 pointer-events-none" : ""}`}>
            {payments.map((p, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`pay-row-${i}`}>
                <select
                  className="col-span-4 h-9 border border-slate-200 rounded-md px-2 text-sm"
                  value={p.method}
                  onChange={(e) => updatePayment(i, { method: e.target.value })}
                  data-testid={`pay-method-${i}`}
                >
                  {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
                <Input
                  className="col-span-3"
                  type="number" min="0" step="0.01"
                  placeholder="Amount"
                  value={p.amount}
                  onChange={(e) => updatePayment(i, { amount: e.target.value })}
                  data-testid={`pay-amount-${i}`}
                />
                <Input
                  className="col-span-4"
                  placeholder="Ref # (optional)"
                  value={p.reference}
                  onChange={(e) => updatePayment(i, { reference: e.target.value })}
                  data-testid={`pay-ref-${i}`}
                />
                <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removePayment(i)} disabled={payments.length === 1} data-testid={`pay-remove-${i}`}>
                  <Trash2 size={14} className="text-rose-600" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addPayment} data-testid="btn-add-payment">
              <Plus size={12} className="mr-1" /> Add Payment Method
            </Button>
          </div>
          <div className="border-t pt-2 mt-1 text-sm flex items-center justify-between">
            <span className="text-slate-500">Sum</span>
            <span className={`font-bold ${paymentSum + 0.01 >= totals.grand_total ? "text-emerald-700" : "text-rose-700"}`} data-testid="pay-sum">
              {fmtMoney(paymentSum)} {paymentSum > totals.grand_total ? `(change ${fmtMoney(paymentSum - totals.grand_total)})` : ""}
            </span>
          </div>
          {needsApproval && !tx.discount_approved_by ? (
            <div className="text-xs bg-rose-50 border border-rose-200 text-rose-800 rounded px-3 py-2">
              ⚠ Discount over 20% — Admin approval required first.
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayOpen(false)}>Cancel</Button>
            <Button
              onClick={submitPay}
              disabled={busy}
              className={proBono ? "bg-amber-600 hover:bg-amber-700" : ""}
              data-testid="btn-submit-pay"
            >
              {proBono ? "Close as Pro Bono" : "Confirm Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Void */}
      <Dialog open={voidOpen} onOpenChange={setVoidOpen}>
        <DialogContent data-testid="void-dialog">
          <DialogHeader>
            <DialogTitle>Void Transaction</DialogTitle>
            <DialogDescription>
              Voiding this transaction will reverse its effects. {tx.status === "paid" ? "If you choose to restore inventory, the medicine stock will be added back." : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Reason</Label>
              <Textarea rows={2} value={voidReason} onChange={(e) => setVoidReason(e.target.value)} data-testid="void-reason" />
            </div>
            {tx.status === "paid" ? (
              <div className="flex items-start gap-2 border rounded p-3 bg-amber-50 border-amber-200" data-testid="restore-inv-row">
                <Checkbox
                  id="restore-inv"
                  checked={restoreInventory}
                  onCheckedChange={(v) => setRestoreInventory(Boolean(v))}
                  data-testid="restore-inv-checkbox"
                />
                <label htmlFor="restore-inv" className="text-sm cursor-pointer">
                  <div className="font-medium text-amber-900">Restore Inventory?</div>
                  <div className="text-xs text-amber-700">
                    Add medicine stocks back to inventory (checked by default).
                  </div>
                </label>
              </div>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVoidOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={submitVoid} disabled={busy} data-testid="btn-submit-void">
              Void Transaction
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve discount */}
      <Dialog open={approveOpen} onOpenChange={setApproveOpen}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve Discount</DialogTitle>
            <DialogDescription>
              This transaction has an effective discount of {totals.effective_transaction_discount_percent}%. Confirm approval as Hospital Admin.
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label>Approval Note (optional)</Label>
            <Textarea rows={2} value={approveNote} onChange={(e) => setApproveNote(e.target.value)} data-testid="approve-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)}>Cancel</Button>
            <Button onClick={approveDiscount} disabled={busy} data-testid="btn-submit-approve">
              <ShieldCheck size={14} className="mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receipt dialog */}
      <Dialog open={receiptOpen} onOpenChange={setReceiptOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="receipt-dialog">
          <DialogHeader>
            <DialogTitle>Receipt · {tx.transaction_number}</DialogTitle>
            <DialogDescription>Preview the receipt before printing.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 p-4" ref={receiptRef}>
            <POSReceiptPreview transaction={tx} settings={printSettings} receiptHeader={receiptHeader} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReceiptOpen(false)}>Close</Button>
            <Button onClick={printReceipt} disabled={printingReceipt} data-testid="btn-print-now">
              <Printer size={14} className="mr-1" /> {printingReceipt ? "Preparing…" : "Print"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medical Certificate form */}
      <Dialog open={mcOpen} onOpenChange={setMcOpen}>
        <DialogContent className="max-w-xl" data-testid="mc-dialog">
          <DialogHeader>
            <DialogTitle>Generate Medical Certificate</DialogTitle>
            <DialogDescription>Same letterhead and signature as the Doctor's RX.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Patient</Label><Input disabled value={tx.patient_snapshot?.full_name || ""} /></div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><Label>Date of Visit</Label>
                <Input type="date" value={mcForm.visit_date} onChange={(e) => setMcForm({...mcForm, visit_date: e.target.value})} data-testid="mc-visit-date" />
              </div>
              <div><Label>Date of Issue</Label>
                <Input type="date" value={mcForm.issue_date} onChange={(e) => setMcForm({...mcForm, issue_date: e.target.value})} data-testid="mc-date" />
              </div>
            </div>
            <div className="text-[11px] text-slate-400">Both default to today. Visit date prints in the body; issue date prints in the “issued on” line.</div>
            <div>
              <Label>Visit date as printed (optional)</Label>
              <Input value={mcForm.date_text} onChange={(e) => setMcForm({...mcForm, date_text: e.target.value})} placeholder="Leave blank to use the visit date above" data-testid="mc-date-text" />
            </div>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div><Label>Age</Label><Input disabled value={tx.patient_snapshot?.age || ""} /></div>
              <div><Label>Sex</Label><Input disabled value={tx.patient_snapshot?.gender || ""} /></div>
              <div><Label>Rest Days</Label>
                <Input type="number" min="0" value={mcForm.rest_days} onChange={(e) => setMcForm({...mcForm, rest_days: e.target.value})} data-testid="mc-rest-days" />
              </div>
            </div>
            <div>
              <Label>Diagnosis</Label>
              <Textarea rows={2} value={mcForm.diagnosis} onChange={(e) => setMcForm({...mcForm, diagnosis: e.target.value})} data-testid="mc-diagnosis" />
            </div>
            <div>
              <Label>Purpose</Label>
              <Input value={mcForm.purpose} onChange={(e) => setMcForm({...mcForm, purpose: e.target.value})} placeholder="e.g. School / Work absence" data-testid="mc-purpose" />
            </div>
            <div>
              <Label>Remarks</Label>
              <Textarea rows={2} value={mcForm.remarks} onChange={(e) => setMcForm({...mcForm, remarks: e.target.value})} data-testid="mc-remarks" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Certificate text (editable)</Label>
                <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setMcForm({ ...mcForm, body_template: DEFAULT_TEMPLATE })}>Reset to default</button>
              </div>
              <Textarea rows={9} value={mcForm.body_template} onChange={(e) => setMcForm({...mcForm, body_template: e.target.value})} data-testid="mc-template-editor" />
              <div className="text-xs text-slate-500 mt-1">
                Preloaded with the standard template. Placeholders auto-fill: {"{{patient_name}}"}, {"{{age}}"}, {"{{gender}}"}, {"{{address}}"}, {"{{date}}"}, {"{{diagnosis}}"}, {"{{rest_days}}"}, {"{{he_she}}"}, {"{{recommendations}}"}, {"{{remarks}}"}, {"{{purpose}}"}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMcOpen(false)}>Cancel</Button>
            <Button onClick={() => generateCertificate(true)} disabled={busy} data-testid="btn-mc-save">
              <Save size={14} className="mr-1" /> Save Medical Certificate
            </Button>
            <Button onClick={async () => {
              await generateCertificate(true);
            }} disabled={busy} data-testid="btn-mc-generate">
              Generate & Print
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Certificate preview */}
      <Dialog open={mcPreviewOpen} onOpenChange={setMcPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="mc-preview-dialog">
          <DialogHeader>
            <DialogTitle>Medical Certificate Preview</DialogTitle>
            <DialogDescription>Use your browser's print dialog to print on letterhead.</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 p-4">
            <MedicalCertificatePreview certificate={activeCert} settings={printSettings} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMcPreviewOpen(false)}>Close</Button>
            <Button onClick={() => window.print()} data-testid="btn-mc-print">
              <Printer size={14} className="mr-1" /> Print Medical Certificate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, red = false, muted = false, big = false, testid }) {
  return (
    <div className="flex items-center justify-between text-sm" data-testid={testid}>
      <span className={`${muted ? "text-slate-400" : "text-slate-600"}`}>{label}</span>
      <span className={`${red ? "text-rose-700" : muted ? "text-slate-400" : "text-slate-900"} ${big ? "font-bold text-lg" : "font-medium"}`}>{value}</span>
    </div>
  );
}

function MedicineAdder({ medicines, onAdd }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const qq = q.toLowerCase().trim();
    return medicines
      .filter((m) => !qq || (m.medicine_name || "").toLowerCase().includes(qq) || (m.brand_name || "").toLowerCase().includes(qq))
      .slice(0, 30);
  }, [medicines, q]);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="btn-add-medicine">
        <Plus size={12} className="mr-1" /> Add Medicine
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="medicine-add-dialog">
          <DialogHeader>
            <DialogTitle>Add Medicine to Bill</DialogTitle>
            <DialogDescription>Pick from your hospital's inventory.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Search medicine..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="med-search" />
          <div className="max-h-72 overflow-auto border rounded divide-y">
            {filtered.length === 0 ? <div className="p-3 text-sm text-slate-500">No matches.</div> :
              filtered.map((m) => (
                <button
                  key={m.id}
                  onClick={() => { onAdd(m); setOpen(false); setQ(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-blue-50"
                  data-testid={`med-pick-${m.id}`}
                >
                  <div className="font-medium text-sm">{m.medicine_name} <span className="text-slate-500 text-xs">{m.strength}</span></div>
                  <div className="text-xs text-slate-500">Stock: {m.stock_quantity || 0} · {fmtMoney(m.unit_price || 0)}</div>
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
